import { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Iletisim() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/site-settings`)
      .then(r => r.json())
      .then(data => { setSettings(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        toast.success('Mesajınız gönderildi!');
      } else {
        toast.error('Mesaj gönderilemedi, tekrar deneyin.');
      }
    } catch {
      toast.error('Bir hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl mb-4 shadow-lg">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            İletişim
          </h1>
          <p className="text-lg text-amber-700">Bizimle iletişime geçin</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-md border border-amber-100 p-8">
              <h2 className="text-2xl font-bold text-amber-900 mb-6">İletişim Bilgileri</h2>

              {!loading && settings && (
                <div className="space-y-5">
                  {settings.address && (
                    <div className="flex items-start gap-4" data-testid="contact-address">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-700 mb-1">Adres</p>
                        <p className="text-amber-900" style={{ whiteSpace: 'pre-wrap' }}>{settings.address}</p>
                      </div>
                    </div>
                  )}
                  {settings.phone && (
                    <div className="flex items-center gap-4" data-testid="contact-phone">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-700 mb-1">Telefon</p>
                        <a href={`tel:${settings.phone}`} className="text-amber-900 hover:text-orange-600 transition-colors">
                          {settings.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  {settings.email && (
                    <div className="flex items-center gap-4" data-testid="contact-email">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-amber-700 mb-1">E-posta</p>
                        <a href={`mailto:${settings.email}`} className="text-amber-900 hover:text-orange-600 transition-colors">
                          {settings.email}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!loading && (!settings || (!settings.address && !settings.phone && !settings.email)) && (
                <p className="text-amber-500 text-center py-4">İletişim bilgisi henüz eklenmemiş.</p>
              )}
            </div>

            {settings?.map_url && (
              <div className="bg-white rounded-2xl shadow-md border border-amber-100 overflow-hidden">
                <iframe
                  src={settings.map_url}
                  title="Konum"
                  className="w-full h-64"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
            )}
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-md border border-amber-100 p-8">
            <h2 className="text-2xl font-bold text-amber-900 mb-6">Bize Mesaj Gönderin</h2>

            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4" data-testid="contact-success">
                <CheckCircle className="w-16 h-16 text-green-500" />
                <h3 className="text-xl font-bold text-amber-900">Mesajınız Alındı!</h3>
                <p className="text-amber-700">En kısa sürede size geri dönüş yapacağız.</p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="border-amber-300 text-amber-900 mt-2"
                >
                  Yeni Mesaj Gönder
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <Label htmlFor="contact-name">Ad Soyad</Label>
                  <Input
                    id="contact-name"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder="Adınız ve soyadınız"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email">E-posta</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    placeholder="ornek@email.com"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-phone">Telefon (Opsiyonel)</Label>
                  <Input
                    id="contact-phone"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    placeholder="0555 000 0000"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-message">Mesajınız</Label>
                  <Textarea
                    id="contact-message"
                    value={formData.message}
                    onChange={e => setFormData({...formData, message: e.target.value})}
                    placeholder="Mesajınızı buraya yazın..."
                    rows={5}
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                  data-testid="contact-submit-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
